import { Menu } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twc } from 'react-twc';

import { ArrowDownImg, AvatarImg, BrandImg, PowerSwitchImg } from '@/assets';
import NetworkSwitcher from '@/components/network-switcher';
import { getChainImage } from '@/hooks/useChain';
import { cn } from '@/utils/styles';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { MobileSheet, MobileSheetClose } from '../mobile-sheet';
import { Button } from '../ui/button';
import { useChainSignInOut } from './useChainSignInOut';
import { useMenus } from './useMenus';

function Header() {
  const { t } = useTranslation();

  const { menus } = useMenus();
  const { chain, accountAddress, shortAccountAddress, ChainIsSignedIn, onSignIn, onSignOut } =
    useChainSignInOut();

  const renderAvatar = (className?: string) => {
    if (!chain) return null;
    return (
      <div
        className={cn(
          'bg-white relative rounded-full bg-opacity-5 h-[44px] w-[44px] flex items-center justify-center cursor-pointer',
          className,
        )}
      >
        <img src={AvatarImg} width={28} height={28} alt="" />
        <img
          className="absolute right-1.5 bottom-1.5"
          src={getChainImage(chain.chainId)}
          width={13}
          height={13}
          alt=""
        />
      </div>
    );
  };

  if (!chain) return null;

  return (
    <header className="py-[18px] px-4 md:px-10 flex justify-between items-center">
      <section className="flex items-center lg:min-w-[300px] justify-start">
        <a href="https://allstake.org/" target="_blank">
          <img src={BrandImg} width="auto" height={40} />
        </a>
      </section>
      <section className="items-center lg:min-w-[300px] justify-center hidden gap-2.5 lg:gap-6 md:flex">
        {menus.map((menu) => (
          <button
            key={menu.label}
            className={cn('text-base font-semibold text-white opacity-60 py-2 px-2 duration-300', {
              'opacity-80 hover:opacity-80': menu.isActive,
              'hover:opacity-100': !menu.isActive,
            })}
            type="button"
            onClick={menu.onClick}
          >
            {menu.label}
          </button>
        ))}
      </section>
      <div className="flex gap-3 md:gap-5">
        <NetworkSwitcher chain={chain} />
        <div className="items-center hidden gap-3 md:gap-5 md:flex">
          {!ChainIsSignedIn && (
            <SignInOutButton className="!justify-center" onClick={onSignIn}>
              {t('header.selectWallet')}
            </SignInOutButton>
          )}
          {ChainIsSignedIn && (
            <AddressWithSignOut
              address={accountAddress}
              shortAccountAddress={shortAccountAddress}
              signOut={onSignOut}
            />
          )}
        </div>
        <div className="flex items-center gap-3 md:hidden">
          {ChainIsSignedIn && (
            <MobileSheet
              trigger={renderAvatar()}
              contentClassName="min-h-[150px]"
              content={
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-2 mb-2">
                    {renderAvatar()}
                    <p className="text-sm font-semibold text-white">{shortAccountAddress}</p>
                  </div>
                  <MobileSheetClose asChild>
                    <Button
                      size="lg"
                      variant="link"
                      className="w-full h-11 text-base font-bold text-[rgba(255,255,255,0.8)] rounded-full bg-[rgba(31,38,54,1)] border-[rgba(255,255,255,0.14)]"
                      onClick={onSignOut}
                    >
                      <img src={PowerSwitchImg} width={24} height={24} />
                      <p>{t('header.disconnect')}</p>
                    </Button>
                  </MobileSheetClose>
                </div>
              }
            />
          )}
          <MobileSheet
            trigger={
              <div className="bg-white rounded-full bg-opacity-5 h-[44px] w-[44px] flex items-center justify-center cursor-pointer">
                <Menu width={24} height={24} />
              </div>
            }
            content={
              <div className="flex flex-col items-start text-base">
                {menus.map((menu) => (
                  <MobileSheetClose
                    key={menu.label}
                    type="button"
                    className={cn(
                      'text-base font-semibold text-white opacity-60 py-2 mb-2 duration-300',
                      {
                        'opacity-80 hover:opacity-80': menu.isActive,
                        'hover:opacity-100': !menu.isActive,
                      },
                    )}
                    onClick={menu.onClick}
                  >
                    {menu.label}
                  </MobileSheetClose>
                ))}

                {!ChainIsSignedIn ? (
                  <MobileSheetClose asChild>
                    <Button
                      size="lg"
                      variant="ghost"
                      className="w-full mt-2 text-base font-bold text-black bg-white rounded-full h-11"
                      onClick={onSignIn}
                    >
                      {t('header.selectWallet')}
                    </Button>
                  </MobileSheetClose>
                ) : (
                  <MobileSheetClose asChild>
                    <Button
                      size="lg"
                      variant="link"
                      className="w-full mt-2 h-11 text-base font-bold text-[rgba(255,255,255,0.8)] rounded-full bg-[rgba(31,38,54,1)] border-[rgba(255,255,255,0.14)]"
                      onClick={onSignOut}
                    >
                      <img src={PowerSwitchImg} width={24} height={24} />
                      <p>{t('header.disconnect')}</p>
                    </Button>
                  </MobileSheetClose>
                )}
              </div>
            }
          />
        </div>
      </div>
    </header>
  );
}

function AddressWithSignOut({
  address,
  shortAccountAddress,
  signOut,
}: {
  address: string;
  shortAccountAddress: string;
  signOut: () => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild className={cn(isOpen && 'token-input-shadow')}>
        <SignInOutButton title={address}>
          {shortAccountAddress}
          <img src={ArrowDownImg} width={20} height={20} />
        </SignInOutButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="w-full" sideOffset={20}>
          <div className="w-full min-w-[160px] rounded-md bg-[#0C111D] border border-[#1F242F] p-2 flex flex-col gap-2">
            <div
              className="flex items-center justify-start gap-2 py-3 px-2 hover:bg-[#1F242F] rounded-md cursor-pointer"
              onClick={signOut}
            >
              <img src={PowerSwitchImg} width={24} height={24} />
              <p>{t('header.signOut')}</p>
            </div>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const SignInOutButton = twc.div`
  px-5 h-[44px] min-w-[160px]
  rounded-full
  bg-white bg-opacity-5
  cursor-pointer select-none
  flex justify-between items-center gap-3
  font-semibold
`;

export default Header;
